/**
 * DHIS2-Inspired Dashboard Theme Constants
 * Color palettes, chart colors, scorecard thresholds, and visualization defaults
 */

// DHIS2-style color palette for charts
export const CHART_COLORS = [
  '#147CD7', // Blue
  '#00897B', // Teal
  '#E56408', // Orange
  '#388E3C', // Green
  '#D32F2F', // Red
  '#7B1FA2', // Purple
  '#1565C0', // Dark Blue
  '#FFC324', // Yellow
  '#4DB6AC', // Light Teal
  '#FF7043', // Deep Orange
  '#5C6BC0', // Indigo
  '#26A69A', // Cyan
];

// Scorecard traffic-light colors
export const SCORECARD_COLORS = {
  excellent: '#388E3C',   // Green 700
  good: '#4CAF50',        // Green 400
  average: '#FFC324',     // Yellow 300
  poor: '#FF9302',        // Orange 500
  critical: '#D32F2F',    // Red 500
  noData: '#E8EDF2',      // Grey 300
};

// Single value card color mappings
export const KPI_COLORS = {
  primary: { bg: '#E3F2FD', text: '#0D47A1', icon: '#147CD7' },
  success: { bg: '#E8F5E9', text: '#1B5E20', icon: '#388E3C' },
  warning: { bg: '#FFF8E1', text: '#6F3205', icon: '#FF9302' },
  danger: { bg: '#FFE5E8', text: '#891515', icon: '#D32F2F' },
  info: { bg: '#E0F2F1', text: '#004D40', icon: '#00897B' },
  purple: { bg: '#F3E5F5', text: '#4A148C', icon: '#7B1FA2' },
};

// Data quality colors
export const QUALITY_COLORS = {
  complete: '#388E3C',
  incomplete: '#FFC324',
  missing: '#D32F2F',
  verified: '#147CD7',
};

// Indicator categories with icons and colors
export const INDICATOR_CATEGORIES = {
  enrollment: { color: '#147CD7', bgColor: '#E3F2FD', label: 'Enrollment', icon: 'People' },
  infrastructure: { color: '#00897B', bgColor: '#E0F2F1', label: 'Infrastructure', icon: 'Business' },
  academic: { color: '#E56408', bgColor: '#FFF3E0', label: 'Academic', icon: 'School' },
  staff: { color: '#7B1FA2', bgColor: '#F3E5F5', label: 'Staff', icon: 'Person' },
  finance: { color: '#388E3C', bgColor: '#E8F5E9', label: 'Financial', icon: 'AttachMoney' },
  demographic: { color: '#1565C0', bgColor: '#E3F2FD', label: 'Demographics', icon: 'Groups' },
  quality: { color: '#FF9302', bgColor: '#FFF8E1', label: 'Quality', icon: 'VerifiedUser' },
  equity: { color: '#D32F2F', bgColor: '#FFE5E8', label: 'Equity', icon: 'Balance' },
};

// Aggregation types
export const AGGREGATION_TYPES = [
  { value: 'SUM', label: 'Sum', description: 'Total of all values' },
  { value: 'COUNT', label: 'Count', description: 'Number of records' },
  { value: 'AVG', label: 'Average', description: 'Mean value' },
  { value: 'MAX', label: 'Maximum', description: 'Highest value' },
  { value: 'MIN', label: 'Minimum', description: 'Lowest value' },
  { value: 'COUNT_DISTINCT', label: 'Count Unique', description: 'Distinct values' },
  { value: 'MEDIAN', label: 'Median', description: 'Middle value' },
  { value: 'STDDEV', label: 'Std Deviation', description: 'Standard deviation' },
];

// Period types
export const PERIOD_TYPES = [
  { value: 'annual', label: 'Annual' },
  { value: 'semester', label: 'Semester' },
  { value: 'term', label: 'Term' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
];

// Visualization types
export const VISUALIZATION_TYPES = [
  { value: 'column', label: 'Column Chart', icon: 'BarChart' },
  { value: 'stacked_column', label: 'Stacked Column', icon: 'StackedBarChart' },
  { value: 'bar', label: 'Bar Chart', icon: 'BarChart' },
  { value: 'line', label: 'Line Chart', icon: 'ShowChart' },
  { value: 'area', label: 'Area Chart', icon: 'AreaChart' },
  { value: 'pie', label: 'Pie Chart', icon: 'PieChart' },
  { value: 'gauge', label: 'Gauge', icon: 'Speed' },
  { value: 'single_value', label: 'Single Value', icon: 'LooksOne' },
  { value: 'scorecard', label: 'Scorecard', icon: 'GridOn' },
  { value: 'pivot_table', label: 'Pivot Table', icon: 'TableChart' },
  { value: 'map', label: 'Map', icon: 'Map' },
];

// Legend presets for scorecards
export const LEGEND_PRESETS = {
  performance: [
    { min: 90, max: 100, color: '#388E3C', label: 'Excellent' },
    { min: 70, max: 89, color: '#4CAF50', label: 'Good' },
    { min: 50, max: 69, color: '#FFC324', label: 'Average' },
    { min: 30, max: 49, color: '#FF9302', label: 'Below Average' },
    { min: 0, max: 29, color: '#D32F2F', label: 'Critical' },
  ],
  completion: [
    { min: 80, max: 100, color: '#388E3C', label: 'On Track' },
    { min: 50, max: 79, color: '#FFC324', label: 'Moderate' },
    { min: 0, max: 49, color: '#D32F2F', label: 'Behind' },
  ],
  ratio: [
    { min: 0, max: 30, color: '#388E3C', label: 'Ideal' },
    { min: 31, max: 45, color: '#4CAF50', label: 'Acceptable' },
    { min: 46, max: 60, color: '#FFC324', label: 'High' },
    { min: 61, max: 100, color: '#FF9302', label: 'Very High' },
    { min: 101, max: 999, color: '#D32F2F', label: 'Critical' },
  ],
  gender_parity: [
    { min: 0.97, max: 1.03, color: '#388E3C', label: 'Parity' },
    { min: 0.90, max: 0.96, color: '#FFC324', label: 'Near Parity' },
    { min: 1.04, max: 1.10, color: '#FFC324', label: 'Near Parity' },
    { min: 0, max: 0.89, color: '#D32F2F', label: 'Disparity' },
    { min: 1.11, max: 2.0, color: '#D32F2F', label: 'Disparity' },
  ],
};

// Format number for display
export const formatNumber = (value: number | null | undefined, type?: string): string => {
  if (value === null || value === undefined) return 'N/A';
  if (type === 'percentage') return `${value.toFixed(1)}%`;
  if (type === 'ratio') return `${value.toFixed(2)}:1`;
  if (type === 'decimal') return value.toFixed(2);
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};

// Get scorecard color based on value and legend
export const getScorecardColor = (
  value: number | null,
  legend: Array<{ min: number; max: number; color: string }>
): string => {
  if (value === null || value === undefined) return SCORECARD_COLORS.noData;
  for (const range of legend) {
    if (value >= range.min && value <= range.max) return range.color;
  }
  return SCORECARD_COLORS.noData;
};
